import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useKnowledgeArticles, KnowledgeArticle } from "@/hooks/useKnowledgeArticles";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { 
  Shield, 
  ArrowLeft, 
  Search, 
  Calendar, 
  Tag, 
  FileText,
  Zap,
  Lock,
  Terminal,
  Bell,
  Server,
  Activity,
  Settings,
  Database,
  Loader2,
  PenSquare
} from "lucide-react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Shield,
  Zap,
  Lock,
  Terminal,
  Bell,
  Server,
  Activity,
  Settings,
  Database
};

export default function KnowledgeHubPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { articles, loading } = useKnowledgeArticles();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc("has_role", { _user_id: user.id, _role: "admin" });

        if (error) throw error;
        setIsAdmin(data === true);
      } catch (err) {
        console.error("Error checking admin role:", err);
        setIsAdmin(false);
      }
    };

    checkAdminRole();
  }, [user]);

  const categories = [...new Set(articles.map(article => article.category))];

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.description?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (article.version?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getIcon = (iconName: string | null) => {
    return iconMap[iconName || "FileText"] || FileText;
  };

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[hsl(224,71%,4%)]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="p-2 bg-primary rounded-lg">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">ServSpect</span>
            </Link>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Link to="/admin/knowledge-hub">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <PenSquare className="w-4 h-4 mr-2" />
                    Manage Articles
                  </Button>
                </Link>
              )}
              <Link to="/">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 border-b border-white/10">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-4">
            <FileText className="w-3 h-3 mr-1" />
            Knowledge Hub
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Release Notes & Updates
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Stay up to date with the latest features, improvements, and security updates for ServSpect.
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 border-b border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search releases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
            </div>
            {categories.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={selectedCategory === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(null)}
                  className={selectedCategory === null ? "bg-primary" : "border-white/20 text-white hover:bg-white/10"}
                >
                  All
                </Button>
                {categories.map(category => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ? "bg-primary" : "border-white/20 text-white hover:bg-white/10"}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Release Articles */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-6">
              {filteredArticles.map((article) => {
                const IconComponent = getIcon(article.icon);
                return (
                  <Card key={article.id} className="bg-white/5 border-white/10 hover:bg-white/[0.07] transition-colors">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-primary/20 rounded-lg">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              {article.version && (
                                <Badge className="bg-primary text-primary-foreground">{article.version}</Badge>
                              )}
                              <Badge variant="outline" className="border-white/20 text-white/70">
                                <Tag className="w-3 h-3 mr-1" />
                                {article.category}
                              </Badge>
                            </div>
                            <CardTitle className="text-xl text-white">{article.title}</CardTitle>
                            <CardDescription className="text-muted-foreground flex items-center gap-2 mt-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(article.created_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {article.description && (
                        <p className="text-muted-foreground mb-4">{article.description}</p>
                      )}
                      {article.highlights && article.highlights.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-white">Highlights:</p>
                          <ul className="grid gap-2 text-sm text-muted-foreground">
                            {article.highlights.map((highlight, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                {highlight}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {!loading && filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No releases found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} ServSpect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
